'use client';

/**
 * LineChart Component
 *
 * Theme-aware line chart with glassmorphism tooltips and gradient fills.
 * Built on Recharts with design system integration.
 *
 * @example
 * ```tsx
 * <LineChart
 *   data={data}
 *   lines={[
 *     { dataKey: 'revenue', name: 'Revenue', color: 'primary' },
 *     { dataKey: 'profit', name: 'Profit', color: 'success' },
 *   ]}
 *   xAxisKey="month"
 * />
 * ```
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';
import { type NeonAccent, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export type ChartColorKey = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

export interface LineConfig {
  /**
   * Data key for this line
   */
  dataKey: string;

  /**
   * Display name for legend/tooltip
   */
  name?: string;

  /**
   * Color key or custom hex color
   */
  color?: ChartColorKey | string;

  /**
   * Line stroke width
   * @default 2
   */
  strokeWidth?: number;

  /**
   * Show area fill under line
   * @default false
   */
  showArea?: boolean;

  /**
   * Area fill opacity
   * @default 0.1
   */
  areaOpacity?: number;

  /**
   * Use gradient fill for area
   * @default true
   */
  gradientFill?: boolean;

  /**
   * Line type
   * @default 'monotone'
   */
  type?: 'linear' | 'monotone' | 'step' | 'stepBefore' | 'stepAfter';

  /**
   * Show dots on data points
   * @default false
   */
  showDots?: boolean;

  /**
   * Dot radius
   * @default 4
   */
  dotRadius?: number;

  /**
   * Dashed line pattern
   */
  strokeDasharray?: string;
}

export interface LineChartProps {
  /**
   * Chart data
   */
  data: Record<string, unknown>[];

  /**
   * Line configurations
   */
  lines: LineConfig[];

  /**
   * Data key for X-axis
   */
  xAxisKey: string;

  /**
   * Chart height
   * @default 300
   */
  height?: number;

  /**
   * Show grid lines
   * @default true
   */
  showGrid?: boolean;

  /**
   * Show X-axis
   * @default true
   */
  showXAxis?: boolean;

  /**
   * Show Y-axis
   * @default true
   */
  showYAxis?: boolean;

  /**
   * Show legend
   * @default false
   */
  showLegend?: boolean;

  /**
   * Show tooltip
   * @default true
   */
  showTooltip?: boolean;

  /**
   * Optional neon accent for styling
   */
  neonAccent?: NeonAccent;

  /**
   * Y-axis domain
   */
  yAxisDomain?: [number | 'auto' | 'dataMin' | 'dataMax', number | 'auto' | 'dataMin' | 'dataMax'];

  /**
   * X-axis tick formatter
   */
  xAxisFormatter?: (value: unknown) => string;

  /**
   * Y-axis tick formatter
   */
  yAxisFormatter?: (value: number) => string;

  /**
   * Tooltip value formatter
   */
  tooltipFormatter?: (value: number, name: string) => [string, string];

  /**
   * Chart margins (all values required in recharts v3)
   */
  margin?: { top: number; right: number; bottom: number; left: number };
}

// ============================================================================
// Styled Components
// ============================================================================

interface TooltipContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const TooltipContainer = styled(
  Box,
  transientOptions,
)<TooltipContainerProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonColors = $neonAccent ? neonAccents[$neonAccent] : null;

  return {
    backgroundColor: neonColors ? ($isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)') : glass.background,
    border: `1px solid ${neonColors ? ($isDark ? neonColors.border : neonColors.borderLight) : glass.border}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    backdropFilter: 'blur(12px)',
    boxShadow: $isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
  };
});

// ============================================================================
// Custom Tooltip
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    name: string;
    color: string;
    dataKey: string;
  }>;
  label?: string;
  isDark: boolean;
  neonAccent?: NeonAccent;
  formatter?: (value: number, name: string) => [string, string];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, isDark, neonAccent, formatter }) => {
  if (!active || !payload?.length) return null;

  return (
    <TooltipContainer $isDark={isDark} $neonAccent={neonAccent}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      {payload.map((entry, index) => {
        const [formattedValue, formattedName] = formatter
          ? formatter(entry.value, entry.name)
          : [String(entry.value), entry.name];

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: index > 0 ? 0.5 : 0,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mr: 1,
              }}
            >
              {formattedName}:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {formattedValue}
            </Typography>
          </Box>
        );
      })}
    </TooltipContainer>
  );
};

// ============================================================================
// Component
// ============================================================================

export const LineChart: React.FC<LineChartProps> = ({
  data,
  lines,
  xAxisKey,
  height = 300,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showLegend = false,
  showTooltip = true,
  neonAccent,
  yAxisDomain,
  xAxisFormatter,
  yAxisFormatter,
  tooltipFormatter,
  margin = { top: 10, right: 30, left: 0, bottom: 0 },
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = useChartColors();

  // Resolve color from key or use custom hex
  const resolveColor = (colorKey?: ChartColorKey | string): string => {
    if (!colorKey) return colors.primary;
    if (colorKey.startsWith('#')) return colorKey;
    return colors[colorKey as ChartColorKey] || colors.primary;
  };

  // Generate stable gradient IDs (memoized to prevent re-renders)
  const gradientIds = useMemo(
    () =>
      lines.reduce(
        (acc, line) => {
          acc[line.dataKey] = `gradient-${line.dataKey}`;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [lines],
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsLineChart data={data} margin={margin}>
        {/* Gradient Definitions */}
        <defs>
          {lines
            .filter((line) => line.showArea && line.gradientFill !== false)
            .map((line) => {
              const color = resolveColor(line.color);
              return (
                <linearGradient key={line.dataKey} id={gradientIds[line.dataKey]} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={line.areaOpacity || 0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              );
            })}
        </defs>

        {/* Grid */}
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />}

        {/* Axes */}
        {showXAxis && (
          <XAxis
            dataKey={xAxisKey}
            stroke={colors.text}
            tick={{ fill: colors.text, fontSize: 12 }}
            tickLine={{ stroke: colors.grid }}
            axisLine={{ stroke: colors.grid }}
            tickFormatter={xAxisFormatter}
          />
        )}
        {showYAxis && (
          <YAxis
            stroke={colors.text}
            tick={{ fill: colors.text, fontSize: 12 }}
            tickLine={{ stroke: colors.grid }}
            axisLine={{ stroke: colors.grid }}
            domain={yAxisDomain}
            tickFormatter={yAxisFormatter}
          />
        )}

        {/* Tooltip */}
        {showTooltip && (
          <Tooltip
            content={<CustomTooltip isDark={isDark} neonAccent={neonAccent} formatter={tooltipFormatter} />}
            cursor={{
              stroke: colors.grid,
              strokeDasharray: '5 5',
            }}
          />
        )}

        {/* Legend */}
        {showLegend && (
          <Legend
            wrapperStyle={{
              paddingTop: 16,
            }}
            formatter={(value) => <span style={{ color: colors.text }}>{value}</span>}
          />
        )}

        {/* Area fills (rendered first, under lines) */}
        {lines
          .filter((line) => line.showArea)
          .map((line) => {
            const color = resolveColor(line.color);
            return (
              <Area
                key={`area-${line.dataKey}`}
                type={line.type || 'monotone'}
                dataKey={line.dataKey}
                stroke="transparent"
                fill={line.gradientFill !== false ? `url(#${gradientIds[line.dataKey]})` : color}
                fillOpacity={line.gradientFill !== false ? 1 : line.areaOpacity || 0.1}
                legendType="none"
              />
            );
          })}

        {/* Lines */}
        {lines.map((line) => {
          const color = resolveColor(line.color);
          return (
            <Line
              key={line.dataKey}
              type={line.type || 'monotone'}
              dataKey={line.dataKey}
              name={line.name || line.dataKey}
              stroke={color}
              strokeWidth={line.strokeWidth || 2}
              strokeDasharray={line.strokeDasharray}
              dot={
                line.showDots
                  ? {
                      r: line.dotRadius || 4,
                      fill: color,
                      stroke: isDark ? '#1a1a1a' : '#fff',
                      strokeWidth: 2,
                    }
                  : false
              }
              activeDot={{
                r: (line.dotRadius || 4) + 2,
                fill: color,
                stroke: isDark ? '#1a1a1a' : '#fff',
                strokeWidth: 2,
              }}
            />
          );
        })}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
};

LineChart.displayName = 'LineChart';

export default LineChart;
